import React from 'react';
import { MapPin, Phone, Mail, Clock, ExternalLink } from 'lucide-react';
import { contactInfo, openingHours, kitchenHours } from '../mock';

export default function Contact() {
  return (
    <div className="bg-[#faf6f0] pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <span className="text-amber-600 tracking-[0.3em] text-xs uppercase">Kommen Sie vorbei</span>
          <h1 className="font-serif text-5xl md:text-6xl text-[#1a1410] mt-4">Kontakt</h1>
          <div className="w-16 h-[3px] bg-amber-500 mx-auto mt-6"/>
          <p className="text-[#5a4a3d] max-w-2xl mx-auto mt-6">
            Für Tischreservierungen und Anfragen erreichen Sie uns am besten telefonisch.
          </p>
        </div>

        {/* Big Call-to-action for phone reservation */}
        <div id="reservierung" className="bg-gradient-to-br from-[#1a1410] to-[#2a1e15] text-white rounded-2xl p-10 md:p-14 mb-12 shadow-2xl relative overflow-hidden">
          <div className="absolute -right-16 -bottom-16 opacity-10 pointer-events-none">
            <Phone size={280}/>
          </div>
          <div className="relative max-w-2xl">
            <span className="text-amber-300 tracking-[0.3em] text-xs uppercase">Tisch reservieren</span>
            <h2 className="font-serif text-3xl md:text-5xl mt-3">Jetzt anrufen &amp; reservieren</h2>
            <p className="mt-4 text-amber-50/85 text-lg">
              Rufen Sie uns direkt an — wir freuen uns auf Ihren Besuch und finden gerne den passenden Tisch für Sie.
            </p>
            <a href={`tel:${contactInfo.phone}`} className="inline-flex items-center gap-3 mt-8 px-8 py-5 rounded-full bg-amber-500 hover:bg-amber-400 text-[#1a1410] font-bold text-xl md:text-2xl shadow-2xl hover:shadow-amber-500/40 hover:-translate-y-0.5 transition-all">
              <Phone size={26}/> {contactInfo.phone}
            </a>
            <p className="mt-4 text-amber-100/70 text-sm">Zu unseren Öffnungszeiten erreichbar.</p>
          </div>
        </div>

        {/* Info cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          <InfoBox icon={MapPin} title="Adresse">
            {contactInfo.address}<br/>{contactInfo.city}<br/>{contactInfo.country}
          </InfoBox>
          <InfoBox icon={Phone} title="Telefon">
            <a href={`tel:${contactInfo.phone}`} className="hover:text-amber-700 text-lg font-medium block">{contactInfo.phone}</a>
            <span className="text-xs">Telefonisch erreichbar</span>
          </InfoBox>
          <InfoBox icon={Mail} title="E-Mail">
            <a href={`mailto:${contactInfo.email}`} className="hover:text-amber-700 break-all">{contactInfo.email}</a>
          </InfoBox>
        </div>

        {/* Map + Hours */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="rounded-2xl overflow-hidden shadow-lg border border-amber-100 aspect-[4/3] lg:aspect-auto lg:min-h-[420px]">
            <iframe
              title="Standort KSV-Sportkantine"
              src="https://www.google.com/maps?q=Otto-Herschmann-Gasse+6,+1110+Wien,+Austria&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="bg-white rounded-2xl p-8 border border-amber-100 shadow-sm">
            <h3 className="font-serif text-2xl text-[#1a1410] flex items-center gap-2 mb-6"><Clock size={22} className="text-amber-500"/> Öffnungszeiten</h3>
            <ul className="space-y-2.5">
              {openingHours.map((o) => (
                <li key={o.day} className="flex justify-between border-b border-amber-50 pb-2 last:border-0">
                  <span className="text-[#3d332b] font-medium">{o.day}</span>
                  <span className={o.hours === 'Ruhetag' ? 'text-red-600 font-medium' : 'text-[#5a4a3d]'}>{o.hours}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-amber-100 flex items-center justify-between bg-amber-50/50 -mx-8 px-8 py-4 -mb-8 rounded-b-2xl">
              <span className="text-xs uppercase tracking-widest text-amber-700 font-bold">Küche</span>
              <span className="text-[#1a1410] font-semibold">{kitchenHours} Uhr</span>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <a href="https://www.google.com/maps/dir/?api=1&destination=Otto-Herschmann-Gasse+6,+1110+Wien,+Austria" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-900 font-medium">
            Route planen <ExternalLink size={16}/>
          </a>
        </div>
      </div>
    </div>
  );
}

function InfoBox({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-amber-100 flex gap-4">
      <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0"><Icon size={22}/></div>
      <div>
        <div className="font-serif text-lg text-[#1a1410] mb-1">{title}</div>
        <div className="text-[#5a4a3d] text-sm">{children}</div>
      </div>
    </div>
  );
}
